import { NgClass } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ValidatorMessageComponent } from '@app/_shared/components/validation-messages/validation-messages.component';
import { MessageModalComponent } from '@app/_shared/modals/message-modal/message-modal.component';
import { InboxDataProviderService } from '@app/_shared/widgets/messages/_data/inbox-data-provider.service';
import { InboxImpressionsService } from '@app/_shared/widgets/messages/_services/inbox-impressions.service';
import { ModelFormGroup } from '@core/models/general-types/general-types';
import { BannerAction } from '@core/models/interfaces/storage-data/banners';
import {
  InboxMessage,
  MessageButton,
  MessageOptionsFieldPopup,
  MessageTextFieldPopup,
} from '@core/models/interfaces/storage-data/inbox';
import { BannersActionHandlerService } from '@core/services/banners-action-handler.service';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { take } from 'rxjs';

type MessageButtonType = 'detailsButton' | 'dismissButton' | 'actionButton';
interface From {
  selectedOption: string | number | null;
}

@Component({
  selector: 'app-message',
  imports: [
    MatTooltip,
    MatIconButton,
    MatIcon,
    FormsModule,
    MatError,
    MatFormField,
    MatOption,
    MatSelect,
    ReactiveFormsModule,
    ValidatorMessageComponent,
    MatButton,
    MatProgressSpinner,
    NgClass,
  ],
  templateUrl: './message.component.html',
  styleUrl: './message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageComponent {
  private el = inject(ElementRef);
  private impressions = inject(InboxImpressionsService);
  private dialog = inject(MatDialog);
  private dataProvider = inject(InboxDataProviderService);
  private actionHandler = inject(BannersActionHandlerService);
  private mixpanel = inject(MixpanelService);
  private destroyRef = inject(DestroyRef);

  // required: the messages widget always binds it — the loose input made
  // every read possibly-undefined (A1.4)
  readonly message = input.required<InboxMessage>();
  readonly close = output<boolean>();

  /**
   * POC-8718/POC-8719: 'Inbox read' fires when the user engages the message,
   * 'Inbox dismiss' ONLY on an explicit dismiss ("Later"/"x") — and each at
   * most once per message, no matter how many flows the interaction crosses.
   */
  private readTracked = false;
  private dismissTracked = false;
  /** One pair of read/delete requests per message; a failed pair may retry. */
  private dismissRequested = false;
  private readRequested = false;

  readonly optionsForm = signal<FormGroup<ModelFormGroup<From>> | null>(null);
  readonly activeButton = signal<MessageButton | null>(null);

  private readonly _impressionObserver = afterNextRender(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void this.trackImpression();
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(this.el.nativeElement);
    this.destroyRef.onDestroy(() => observer.disconnect());
  });

  private initOptionsForm() {
    this.optionsForm.set(
      new FormGroup<ModelFormGroup<From>>({
        selectedOption: new FormControl(null, [Validators.required]),
      })
    );
  }

  onClick(buttonType: MessageButtonType) {
    if (buttonType === 'detailsButton') {
      this.trackRead();
      this.dialog
        .open(MessageModalComponent, {
          panelClass: 'default-modal',
          data: { message: this.message() },
        })
        .afterClosed()
        .pipe(take(1))
        // The modal reports WHICH control closed it, and only two of them mean
        // anything happened: "Back", Esc and the backdrop leave the message
        // exactly as it was. The old code ran the ACTION button on every
        // close — a "Back" click navigated the user away (POC-8213's class).
        .subscribe((result: unknown) => {
          if (result === 'action') this.onClickSuccess('actionButton');
          else if (result === 'dismiss') this.onClickSuccess('dismissButton');
          else if (
            !!result &&
            typeof result === 'object' &&
            'responseValue' in result
          ) {
            this.sendLogicResponse(result.responseValue as string);
          }
        });
    } else {
      this.onClickSuccess(buttonType);
    }
  }

  private onClickSuccess(buttonType: MessageButtonType) {
    const isDismiss = buttonType === 'dismissButton';
    const button = isDismiss
      ? this.message().dismissButton
      : this.message().actionButton;

    if (isDismiss) this.trackDismiss();
    else this.trackRead();

    // R151. A message may carry `canDismiss` without a dismissButton; the
    // pair then goes out under the message's own id — the fallback the
    // legacy contract always had. Reading `.actionId` off the missing
    // button used to throw here and leave the message undismissable.
    if (!button) {
      if (isDismiss) this.sendDismissRequests(undefined, true);
      return;
    }

    void this.handleButtonClick(button);
  }

  private trackRead() {
    if (this.readTracked) return;
    this.readTracked = true;
    this.mixpanel.track('Inbox read', this.getMessageTrackingProps());
  }

  private trackDismiss() {
    if (this.dismissTracked) return;
    this.dismissTracked = true;
    this.mixpanel.track('Inbox dismiss', this.getMessageTrackingProps());
  }

  private async handleButtonClick(button: MessageButton): Promise<void> {
    if (button.actionId !== 'sendActionForLogic') {
      await this.actionHandler.handleAction({
        from: 'Inbox',
        isBanner: false,
        banner: { title: this.message().title, action: button as BannerAction },
      });
      this.sendDismissRequests(button, true);
      return;
    }

    const hasTextPopup = !!button.actionParams?.textFieldPopup;
    const hasOptions = !!button.actionParams?.options;
    const hash = button.actionParams?.hash;

    this.activeButton.set(button);

    if (hash && hasTextPopup) {
      // The answer form lives in the details modal (Figma 1898-128286,
      // groups 41/42); a bodyless message opens the modal straight in form
      // mode. The modal closes with { responseValue } on Save.
      this.dialog
        .open(MessageModalComponent, {
          panelClass: 'default-modal',
          data: { message: this.message(), openLogicForm: true },
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((result: unknown) => {
          if (
            !!result &&
            typeof result === 'object' &&
            'responseValue' in result
          ) {
            this.sendLogicResponse(result.responseValue as string);
          }
        });
    } else if (hash && hasOptions) {
      this.initOptionsForm();
    } else {
      this.sendDismissRequests(button);
    }
  }

  /** The text-popup answer, collected by the details modal. */
  private sendLogicResponse(responseValue: string) {
    this.dataProvider
      .inboxSendActionForLogic({
        hash:
          this.activeButton()?.actionParams?.hash ??
          this.message().actionButton?.actionParams?.hash,
        responseValue,
      })
      .pipe(take(1))
      .subscribe({
        next: () => this.sendDismissRequests(this.message().actionButton),
        // Failures are reported by the global error handler
        error: (): undefined => undefined,
      });
  }

  saveOption() {
    const form = this.optionsForm();
    if (!form || form.disabled) return;

    form.disable();

    this.dataProvider
      .inboxSendActionForLogic({
        hash: this.activeButton()?.actionParams.hash,
        selectedOption: form.getRawValue().selectedOption,
      })
      .pipe(take(1))
      .subscribe({
        next: () => this.sendDismissRequests(this.activeButton() ?? undefined),
        error: () => form.enable(),
      });
  }

  /**
   * The blue-dot click: mark the message read (POC-6743's contract — only the
   * dot or the buttons make a message read, never the message body), and let
   * the reset refetch of the inbox container update the dot and the bell
   * counter from the server's answer.
   */
  sendReadRequest() {
    if (this.readRequested) return;
    this.readRequested = true;

    this.dataProvider
      .inboxReadAlerts({
        alertsIds: [`${this.message().id}`],
        updateCollection: true,
      })
      .pipe(take(1))
      .subscribe({
        next: () => this.trackRead(),
        error: () => (this.readRequested = false),
      });
  }

  /**
   * The legacy tail of every completed interaction: readAlerts (fire-and-forget,
   * as the legacy app sent it) plus deleteAlerts, whose response drives the
   * refetch (`tapReloadOrInbox`). Keyed on the button's `alertId` — the wire
   * contract legacy used — with the message id as the fallback. The latch keeps
   * the pair single-shot per message (POC-8719's duplicate class); a failed
   * delete re-arms it so the user can retry.
   */
  private sendDismissRequests(button?: MessageButton, afterClose = false) {
    if (this.dismissRequested) {
      if (afterClose) this.close.emit(true);
      return;
    }
    this.dismissRequested = true;

    const alertId = `${button?.alertId ?? this.message().id}`;

    this.dataProvider
      .inboxReadAlerts({ alertsIds: [alertId] })
      .pipe(take(1))
      // Fire-and-forget, as legacy sent it: a failure is toasted and changes
      // nothing here.
      .subscribe({ error: (): undefined => undefined });

    this.dataProvider
      .inboxDeleteAlerts({ alerts: [{ alertId }] })
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (afterClose) this.close.emit(true);
        },
        error: () => (this.dismissRequested = false),
      });
  }

  private getLogicPopupData(
    button: MessageButton
  ): MessageTextFieldPopup | MessageOptionsFieldPopup | null {
    const params = button.actionParams;
    if (!params) return null;
    if (params.textFieldPopup) return params.textFieldPopup;
    if (params.options) {
      return {
        title: params.title ?? '',
        subtitle: params.subtitle ?? '',
        options: params.options,
        cancelButtonTitle: 'Cancel',
        confirmButtonTitle: 'Save',
      };
    }
    return null;
  }

  /**
   * One "Inbox impression" per message per session, the session ending at
   * sign-out ("Mixpanel > Inboxes"). `InboxImpressionsService` remembers the
   * ids in IndexedDB; the store slice this used to read was never written.
   */
  private async trackImpression() {
    if (await this.impressions.markShown(this.message().id)) {
      this.mixpanel.track('Inbox impression', this.getMessageTrackingProps());
    }
  }

  private getMessageTrackingProps() {
    return {
      'Inbox ID': this.message().id,
      'Inbox title': this.message().title,
      'Has image': !!this.message().imageUrl,
    };
  }
}
